import { useState } from 'react';
import { REGIONS, YEARS } from '@/lib/catalog';

export interface FilterValue {
  genres: string[];
  region: string;
  year: string;
  sort: 'newest' | 'popular';
}

interface FilterBarProps {
  genreOptions: string[];
  value: FilterValue;
  onChange: (value: FilterValue) => void;
  onReset: () => void;
}

const SORT_OPTIONS: { value: 'newest' | 'popular'; label: string }[] = [
  { value: 'newest', label: '最新' },
  { value: 'popular', label: '热门' }
];

export default function FilterBar({ genreOptions, value, onChange, onReset }: FilterBarProps) {
  const [open, setOpen] = useState(false);

  const toggleGenre = (genre: string) => {
    const genres = value.genres.includes(genre)
      ? value.genres.filter((g) => g !== genre)
      : [...value.genres, genre];
    onChange({ ...value, genres });
  };

  const chipClass = (active: boolean) =>
    `rounded-full px-3 py-1.5 text-xs transition whitespace-nowrap md:text-sm ${
      active
        ? 'bg-primary-500 text-background-50'
        : 'border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600'
    }`;

  const activeCount = value.genres.length + (value.region ? 1 : 0) + (value.year ? 1 : 0);

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
      <div className="flex items-center justify-between gap-3 lg:mb-4">
        <div className="flex items-center gap-2">
          <i className="ri-filter-3-line text-lg text-primary-600" />
          <span className="font-heading text-sm font-bold text-foreground-900">筛选</span>
          {activeCount > 0 && (
            <span className="rounded-full bg-accent-100 px-2 py-0.5 text-xs font-medium text-accent-900">
              已选 {activeCount}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onReset}
            className="flex items-center gap-1 rounded-md px-3 py-1.5 text-xs text-foreground-600 transition hover:bg-background-100 hover:text-primary-600 md:text-sm whitespace-nowrap"
          >
            <i className="ri-refresh-line" />
            重置
          </button>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="flex h-9 items-center gap-1 rounded-md border border-background-300 px-3 text-xs text-foreground-700 transition hover:border-primary-300 lg:hidden"
          >
            <i className={`${open ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-base`} />
            {open ? '收起' : '展开'}
          </button>
        </div>
      </div>

      <div className={`${open ? 'block' : 'hidden'} space-y-4 pt-4 lg:block lg:pt-0`}>
        <div className="flex flex-col gap-2 md:flex-row md:items-start">
          <span className="w-14 shrink-0 pt-1.5 text-xs text-foreground-500 md:text-sm">类型</span>
          <div className="flex flex-wrap gap-2">
            {genreOptions.map((genre) => (
              <button key={genre} type="button" onClick={() => toggleGenre(genre)} className={chipClass(value.genres.includes(genre))}>
                {genre}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-2 md:flex-row md:items-start">
          <span className="w-14 shrink-0 pt-1.5 text-xs text-foreground-500 md:text-sm">地区</span>
          <div className="flex flex-wrap gap-2">
            {REGIONS.map((region) => (
              <button
                key={region}
                type="button"
                onClick={() => onChange({ ...value, region: value.region === region ? '' : region })}
                className={chipClass(value.region === region)}
              >
                {region}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-2 md:flex-row md:items-start">
          <span className="w-14 shrink-0 pt-1.5 text-xs text-foreground-500 md:text-sm">年份</span>
          <div className="flex flex-wrap gap-2">
            {YEARS.map((year) => (
              <button
                key={year}
                type="button"
                onClick={() => onChange({ ...value, year: value.year === year ? '' : year })}
                className={chipClass(value.year === year)}
              >
                {year}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-2 md:flex-row md:items-start">
          <span className="w-14 shrink-0 pt-1.5 text-xs text-foreground-500 md:text-sm">排序</span>
          <div className="flex flex-wrap gap-2">
            {SORT_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => onChange({ ...value, sort: option.value })}
                className={chipClass(value.sort === option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}